import { useId, useRef, useState, type CSSProperties } from "react";
import { Text } from "@blycare/polar-ui";
import { useBoundField, type FieldProps } from "../field-state";
import "./style.css";

/**
 * 原生 range 滑块。草稿里 iconSize 是 number、其余尺寸字段是 string，
 * 所以按现值类型回写，避免改动存到 API 的 JSON 形状。
 *
 * 拖动中只更新本组件、表单回写按帧合并成一次：输入事件比屏幕刷新密得多，逐个回写会连带
 * 重渲染整个编辑器（预览、取色行、校验），主线程排满后滑块明显跟不上指针。
 */
export function SliderField({
  label,
  min = 0,
  max = 100,
  step = 1,
  ...props
}: FieldProps<"input"> & { label: string }) {
  const { field, error, dismissError, handleBlur } = useBoundField<number | string>();
  const id = useId();
  const from = Number(min);
  const to = Number(max);
  const stored = Number(field.state.value);
  const value = Number.isFinite(stored) ? Math.min(Math.max(stored, from), to) : from;
  // 拖动中的显示值；null 表示跟随表单。回写要的是 DOM 最新值，直接从 input 上读，
  // 不另存一份。
  const [dragging, setDragging] = useState<number | null>(null);
  const frame = useRef(0);
  const shown = dragging ?? value;
  const progress = to > from ? (shown - from) / (to - from) : 0;

  // 一帧只回写一次；写完把显示交还给表单值。
  const write = (input: HTMLInputElement) => {
    frame.current = 0;
    setDragging(null);
    field.handleChange(typeof field.state.value === "number" ? Number(input.value) : input.value);
  };

  return (
    <div className="slider-field" style={{ "--slider-progress": progress } as CSSProperties}>
      <label htmlFor={id}>{label}</label>
      <div className="slider-field__track">
        <span className="slider-field__fill" />
        <span className="slider-field__bubble">{shown}</span>
        <input
          {...props}
          id={id}
          className="slider-field__input"
          type="range"
          name={field.name}
          min={from}
          max={to}
          step={step}
          value={shown}
          aria-describedby={error ? `${id}-error` : undefined}
          onFocus={dismissError}
          onInput={(event) => {
            dismissError();
            const input = event.currentTarget;
            setDragging(Number(input.value));
            frame.current ||= requestAnimationFrame(() => write(input));
          }}
          onBlur={handleBlur}
        />
      </div>
      {error ? (
        <Text as="p" variant="bodySm" tone="critical" id={`${id}-error`}>
          {error}
        </Text>
      ) : null}
    </div>
  );
}
